<template>
    <main class="client-edit-page">
        <section class="client-edit-card">
            <header class="client-edit-header">
                <div class="client-edit-title">
                    <el-button class="client-back-button" icon="el-icon-arrow-left"
                               @click.native.prevent="goBack"></el-button>
                    <h1>Update client</h1>
                </div>
                <div class="client-edit-actions">
                    <el-button class="client-delete-button" @click="openDeleteClientDialog" type="danger"
                               plain>Delete</el-button>
                    <el-button class="client-save-button" type="primary" :disabled="buttonDisabledStatus"
                               @click.native.prevent="updateClient">Save</el-button>
                </div>
            </header>

            <el-form class="client-modern-form" label-width="0px" :model="objectModel" :rules="validRules"
                     v-loading="loading" ref="objectModel">
                <section class="client-form-section">
                    <div class="client-section-header">
                        <div class="client-section-title">
                            <i class="el-icon-user client-section-icon"></i>
                            <span>Basic information</span>
                        </div>
                    </div>
                    <div class="client-section-content">
                        <el-form-item class="client-form-row">
                            <span class="client-form-label">Name</span>
                            <el-input class="client-line-input" v-model="objectModel.name"
                                      placeholder="Enter a name..."></el-input>
                        </el-form-item>
                        <el-form-item class="client-form-row" prop="clientEmail">
                            <span class="client-form-label">Email</span>
                            <el-input class="client-line-input" v-model="objectModel.clientEmail"
                                      placeholder="Enter an email address..."></el-input>
                        </el-form-item>
                        <el-form-item class="client-form-row">
                            <span class="client-form-label">Phone</span>
                            <el-input class="client-line-input" v-model="objectModel.phone"
                                      placeholder="Enter a phone number..."></el-input>
                        </el-form-item>
                    </div>
                </section>

                <section class="client-form-section">
                    <div class="client-section-header">
                        <div class="client-section-title">
                            <i class="el-icon-document client-section-icon"></i>
                            <span>Billing address</span>
                        </div>
                    </div>
                    <div class="client-section-content">
                        <el-form-item class="client-form-row">
                            <span class="client-form-label">Line 1</span>
                            <el-input class="client-line-input" v-model="objectModel.billingAddressLine1"></el-input>
                        </el-form-item>
                        <el-form-item class="client-form-row">
                            <span class="client-form-label">Line 2</span>
                            <el-input class="client-line-input" v-model="objectModel.billingAddressLine2"></el-input>
                        </el-form-item>
                        <el-form-item class="client-form-row">
                            <span class="client-form-label">Line 3</span>
                            <el-input class="client-line-input" v-model="objectModel.billingAddressLine3"></el-input>
                        </el-form-item>
                    </div>
                </section>

                <section class="client-form-section">
                    <div class="client-section-header">
                        <div class="client-section-title">
                            <i class="el-icon-location-outline client-section-icon"></i>
                            <span>Shipping address</span>
                        </div>
                        <el-checkbox v-model="sameAsBilling" class="client-same-checkbox">Same as billing address</el-checkbox>
                    </div>
                    <div class="client-section-content">
                        <el-form-item class="client-form-row">
                            <span class="client-form-label">Shipping name</span>
                            <el-input class="client-line-input" v-model="objectModel.shippingName"></el-input>
                        </el-form-item>
                        <el-form-item class="client-form-row">
                            <span class="client-form-label">Line 1</span>
                            <el-input class="client-line-input" v-model="objectModel.shippingAddressLine1"
                                      :disabled="sameAsBilling"></el-input>
                        </el-form-item>
                        <el-form-item class="client-form-row">
                            <span class="client-form-label">Line 2</span>
                            <el-input class="client-line-input" v-model="objectModel.shippingAddressLine2"
                                      :disabled="sameAsBilling"></el-input>
                        </el-form-item>
                        <el-form-item class="client-form-row">
                            <span class="client-form-label">Line 3</span>
                            <el-input class="client-line-input" v-model="objectModel.shippingAddressLine3"
                                      :disabled="sameAsBilling"></el-input>
                        </el-form-item>
                        <el-form-item class="client-form-row client-note-row">
                            <span class="client-form-label">Notes</span>
                            <el-input class="client-line-textarea" type="textarea" autosize v-model="objectModel.note"
                                      placeholder="Add notes about the client. They can't see these"></el-input>
                        </el-form-item>
                    </div>
                </section>
            </el-form>
        </section>
    </main>
</template>


<script>
    import {timest, validateEmail} from '@/utils/validate'

    export default {
        data() {
            const validEmail = (rule, value, callback) => {
                if (value.length > 0 && !validateEmail(value)) {
                    callback(new Error('Please enter a valid email address'))
                } else {
                    callback()
                }
            }
            return {
                objectModel: {
                    name: '',
                    clientEmail: '',
                    phone: '',
                    billingAddressLine1: '',
                    billingAddressLine2: '',
                    billingAddressLine3: '',
                    shippingName: '',
                    shippingAddressLine1: '',
                    shippingAddressLine2: '',
                    shippingAddressLine3: '',
                    note: ''
                },
                sameAsBilling: false,
                loading: true,
                buttonDisabledStatus: false,
                validRules: {
                    clientEmail: [{trigger: ['blur', 'change'], validator: validEmail}],
                }
            };
        },
        watch: {
            sameAsBilling(value) {
                if (value) {
                    this.syncShippingAddress();
                }
            },
            'objectModel.billingAddressLine1'() {
                this.syncShippingAddressIfNeeded();
            },
            'objectModel.billingAddressLine2'() {
                this.syncShippingAddressIfNeeded();
            },
            'objectModel.billingAddressLine3'() {
                this.syncShippingAddressIfNeeded();
            }
        },
        methods: {
            syncShippingAddressIfNeeded() {
                if (this.sameAsBilling) {
                    this.syncShippingAddress();
                }
            },
            syncShippingAddress() {
                this.objectModel.shippingAddressLine1 = this.objectModel.billingAddressLine1;
                this.objectModel.shippingAddressLine2 = this.objectModel.billingAddressLine2;
                this.objectModel.shippingAddressLine3 = this.objectModel.billingAddressLine3;
            },
            goBack() {
                let self = this;
                self.$router.push('/client');
            },
            /////////////////////////////////////////////加载数据/////////////////////////////////////////////
            queryClient() {
                let that = this;
                this.$axios.post('query_user_client', {
                    params: {"innerUUID": this.$route.query.innerUUID}
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        that.objectModel = response.data.objectData;
                    }
                    that.loading = false;
                })
            },
            /////////////////////////////////////////////加载数据/////////////////////////////////////////////

            /////////////////////////////////////////////删除逻辑/////////////////////////////////////////////
            //打开删除框
            openDeleteClientDialog() {
                this.$confirm('This will delete the client. Continue?', 'Warning', {
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                }).then(() => {
                    this.deleteClient();
                })
            },
            //删除client
            deleteClient() {
                let self = this;
                this.loading = true;
                self.buttonDisabledStatus = false;
                this.$axios.post('update_user_client', {
                    params: {
                        isDelete: 1,
                        innerUUID: this.$route.query.innerUUID,
                        accessDate: timest()
                    }
                }).then(response => {
                    this.loading = false;
                    self.buttonDisabledStatus = true;
                    if (response.data.errorCode === 0) {
                        self.$router.push('/client');
                    }
                }).catch(function () {
                    self.loading = false;
                    self.buttonDisabledStatus = true;
                })
            },
            /////////////////////////////////////////////删除逻辑/////////////////////////////////////////////

            /////////////////////////////////////////////修改逻辑/////////////////////////////////////////////
            //修改client
            updateClient() {
                let self = this;
                this.$refs.objectModel.validate((valid) => {
                    if (valid) {
                        this.loading = true;
                        self.buttonDisabledStatus = true;
                        this.$axios.post('update_user_client', {
                            params: {
                                name: this.objectModel.name,
                                clientEmail: this.objectModel.clientEmail,
                                phone: this.objectModel.phone,
                                billingAddressLine1: this.objectModel.billingAddressLine1,
                                billingAddressLine2: this.objectModel.billingAddressLine2,
                                billingAddressLine3: this.objectModel.billingAddressLine3,
                                shippingName: this.objectModel.shippingName,
                                shippingAddressLine1: this.objectModel.shippingAddressLine1,
                                shippingAddressLine2: this.objectModel.shippingAddressLine2,
                                shippingAddressLine3: this.objectModel.shippingAddressLine3,
                                note: this.objectModel.note,
                                innerUUID: this.$route.query.innerUUID,
                                accessDate: timest()
                            }
                        }).then(response => {
                            this.loading = false;
                            self.buttonDisabledStatus = false;
                            if (response.data.errorCode === 0) {
                                self.$router.push('/client');
                            }
                        }).catch(function () {
                            self.loading = false;
                            self.buttonDisabledStatus = false;
                        })
                    } else {
                        return false;
                    }
                });
            }
            /////////////////////////////////////////////修改逻辑/////////////////////////////////////////////
        },
        mounted() {
            this.queryClient();
        }
    }
</script>
